import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Layers, Pencil, Send, Archive, ListChecks, Trophy, Users, Unlock, Calendar,
  Lock, CheckCircle2,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, StatsCard, StatsGrid, AppButton, EmptyState, Callout,
  StatusBadge, AppDataTable, sortRows,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import { seriesTrail } from "../QuizBank/quizCenterTrail";

/**
 * TestSeriesDetailsPage — one series: its member tests and its leaderboard.
 *
 * ⚠️ Two tables, and they get DIFFERENT controls, for a reason worth keeping:
 *
 *  - **Member tests are ORDERED, and the order is the content.** In a
 *    `sequential` series the row above is the test that unlocks the row below,
 *    so a sort control would offer to destroy the one thing the column means.
 *    Chrome only — no sort, no filters, no resize.
 *  - **The leaderboard is a ranking someone re-reads by different measures**
 *    ("who has done the most tests", "who is averaging best"), so sorting is on.
 *    No filters: it is a top-N list, not a list to search.
 *
 * Neither paginates. Both are short by construction, and a paginator under six
 * rows is furniture. See `docs/design-system/TABLE_MIGRATION.md`.
 *
 * ⚠️ No `TestSeries.scss` import. The sheet stays for `TestSeriesPage` and
 * `CreateTestSeriesPage`, which still render `ts-chip`, `ts-meta-item` and
 * `ts-status`; the four rules only this page used were deleted with it.
 */

const UNLOCK_LABELS = { open: "Open", scheduled: "Scheduled", sequential: "Sequential" };
const UNLOCK_ICONS = { open: Unlock, scheduled: Calendar, sequential: Lock };
const ALL_BRANCH = { headers: { "X-Branch-Id": "all" } };

const DASH = <span className="tw-text-content-subtle">—</span>;

/** The heading strip above each of this page's two tables. Not a component in
 *  `ui/` — it is a label with an icon, it has no other call site, and the
 *  utilities on it say what it is. */
const SectionTitle = ({ icon: Icon, children }) => (
  <span className="tw-flex tw-items-center tw-gap-2 tw-text-[0.8125rem] tw-font-semibold tw-text-content">
    <Icon size={15} /> {children}
  </span>
);

/** #1 / #2 / #3 get a medal tint; everyone else gets the neutral chip. The three
 *  ranks are the only ones anyone reads off a leaderboard at a glance. */
const RANK_CLASSES = {
  1: "tw-bg-warning-soft tw-text-warning-fg",
  2: "tw-bg-muted tw-text-content",
  3: "tw-bg-danger-soft tw-text-danger-fg",
};

const TestSeriesDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [series, setSeries] = useState(null);
  const [board, setBoard] = useState({ leaderboard: [], enrolled: 0 });
  const [loading, setLoading] = useState(true);
  const [boardSort, setBoardSort] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = useCallback(
    (message, type = "success") => setToast({ show: true, message, type }),
    [],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/test-series/${id}`, ALL_BRANCH);
      setSeries(data.series);
      if (data.series?.leaderboardEnabled) {
        const lb = await apiClient.get(`/test-series/${id}/leaderboard`, ALL_BRANCH).catch(() => ({ data: {} }));
        setBoard({ leaderboard: lb.data.leaderboard || [], enrolled: lb.data.enrolled || 0 });
      }
    } catch {
      showToast("Failed to load series", "error");
    }
    setLoading(false);
  }, [id, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      showToast(okMsg);
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.message || "Action failed", "error");
    }
  };

  /** Sorted by `order` here rather than at render: this IS the series' sequence,
   *  and the table below must not offer to reorder it. */
  const tests = useMemo(
    () => [...(series?.tests || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [series],
  );

  const testColumns = useMemo(() => [
    {
      key: "index",
      label: "#",
      width: 56,
      render: (t, i) => <span className="tw-tabular-nums tw-text-content-subtle">{i + 1}</span>,
    },
    {
      key: "test",
      label: "Test",
      render: (t) => (
        <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-font-medium tw-text-content">
            {t.assessment?.title || t.label || "—"}
          </span>
          {t.isOptional && <StatusBadge status="neutral">Optional</StatusBadge>}
          {series?.unlockMode === "scheduled" && t.unlockDate && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[0.75rem] tw-text-content-subtle">
              {/* ⚠️ IST explicitly — the box is UTC and a bare toLocaleDateString
                  names the previous day for anything after 05:30. */}
              <Calendar size={12} />
              {new Date(t.unlockDate).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}
            </span>
          )}
        </span>
      ),
    },
    { key: "mode", label: "Mode", render: (t) => t.assessment?.mode || DASH },
    {
      key: "questions",
      label: "Questions",
      align: "right",
      render: (t) => (t.assessment?.questionCount ?? null) !== null
        ? <span className="tw-tabular-nums">{t.assessment.questionCount}</span>
        : DASH,
    },
    {
      key: "status",
      label: "Status",
      render: (t) => (t.assessment?.status === "published" ? (
        <StatusBadge status="success">
          <CheckCircle2 size={11} /> Published
        </StatusBadge>
      ) : (
        // "missing" is a real state here — a series row whose assessment was
        // deleted out from under it — so it must not read as a draft.
        <StatusBadge status={t.assessment?.status ? "pending" : "danger"}>
          {t.assessment?.status || "missing"}
        </StatusBadge>
      )),
    },
  ], [series]);

  const boardColumns = useMemo(() => [
    {
      key: "rank",
      label: "Rank",
      width: 84,
      sortable: true,
      sortValue: (r) => r.rank,
      render: (r) => (
        <span
          className={`tw-inline-flex tw-items-center tw-justify-center tw-min-w-[2.25rem] tw-rounded-sm tw-px-1.5 tw-py-0.5 tw-text-[0.75rem] tw-font-semibold tw-tabular-nums ${
            RANK_CLASSES[r.rank] || "tw-bg-muted tw-text-content-subtle"
          }`}
        >
          #{r.rank}
        </span>
      ),
    },
    {
      key: "name",
      label: "Student",
      sortable: true,
      sortValue: (r) => r.name || "",
      render: (r) => <span className="tw-font-medium tw-text-content">{r.name}</span>,
    },
    {
      key: "testsCompleted",
      label: "Tests done",
      align: "right",
      sortable: true,
      sortValue: (r) => r.testsCompleted ?? 0,
      render: (r) => <span className="tw-tabular-nums">{r.testsCompleted}</span>,
    },
    {
      key: "totalScore",
      label: "Total score",
      align: "right",
      sortable: true,
      sortValue: (r) => r.totalScore ?? 0,
      render: (r) => <span className="tw-tabular-nums">{r.totalScore}</span>,
    },
    {
      key: "avgPercentage",
      label: "Avg %",
      align: "right",
      sortable: true,
      sortValue: (r) => r.avgPercentage ?? 0,
      render: (r) => <span className="tw-tabular-nums">{r.avgPercentage}%</span>,
    },
    {
      key: "percentile",
      label: "Percentile",
      align: "right",
      sortable: true,
      sortValue: (r) => r.percentile ?? 0,
      render: (r) => <span className="tw-tabular-nums">{r.percentile}</span>,
    },
  ], []);

  const boardRows = useMemo(
    () => sortRows(board.leaderboard, boardColumns, boardSort),
    [board.leaderboard, boardColumns, boardSort],
  );

  // A shimmer mirroring the layout, not a spinner — the app's loading
  // convention. The page cannot render its own header until the series is in.
  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        <Shimmer height={72} />
        <Shimmer height={96} />
        <Shimmer height={220} />
      </div>
    );
  }
  if (!series) return <EmptyState icon={<Layers size={40} />} title="Series not found" />;

  const UnlockIcon = UNLOCK_ICONS[series.unlockMode] || Unlock;
  const allPublished = tests.every((t) => t.assessment?.status === "published");

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader
        icon={Layers}
        title={series.title}
        description={series.description || `${UNLOCK_LABELS[series.unlockMode]} series`}
        breadcrumbs={seriesTrail({ id: series._id, title: series.title })}
        actions={
          <div className="tw-flex tw-items-center tw-gap-2">
            <AppButton variant="outline" onClick={() => navigate(`/test-series/edit/${series._id}`)}>
              <Pencil size={15} /> Edit
            </AppButton>
            {series.status === "draft" && (
              <AppButton variant="default" onClick={() => act(() => apiClient.put(`/test-series/${series._id}/publish`), "Series published!")}>
                <Send size={15} /> Publish
              </AppButton>
            )}
            {series.status === "published" && (
              <AppButton variant="outline" onClick={() => act(() => apiClient.put(`/test-series/${series._id}/archive`), "Series archived")}>
                <Archive size={15} /> Archive
              </AppButton>
            )}
          </div>
        }
      />

      {/* Was a hand-rolled box with `rgba(255,193,7,0.1)` written inline — a
          colour literal in JS, which is the same violation as one in a
          stylesheet. `Callout` carries the tone and the icon. */}
      {series.status === "draft" && !allPublished && tests.length > 0 && (
        <Callout tone="warning">
          Some member tests are still drafts. Every test must be published before
          this series can go live.
        </Callout>
      )}

      <StatsGrid>
        <StatsCard title="Tests" value={series.testCount || tests.length} icon={ListChecks} iconColor="blue" />
        <StatsCard title="Status" value={series.status} icon={Send} iconColor={series.status === "published" ? "emerald" : "amber"} />
        <StatsCard title="Progression" value={UNLOCK_LABELS[series.unlockMode]} icon={UnlockIcon} iconColor="violet" />
        {series.leaderboardEnabled && <StatsCard title="Participants" value={board.enrolled} icon={Users} iconColor="amber" />}
      </StatsGrid>

      {/* ⚠️ The section heading goes in the table's own `toolbar`, not in an
          `AppCard` around it. `AppDataTable` IS a card — same radius, border and
          elevation as `AppCard` — so wrapping one in the other draws a card
          inside a card, and the inner border lands a padding-width inside the
          outer one. */}
      <AppDataTable
        fill={false}
        toolbar={<SectionTitle icon={ListChecks}>Tests ({tests.length})</SectionTitle>}
        columns={testColumns}
        rows={tests}
        rowKey={(t, i) => t._id || i}
        emptyMessage="No tests added. Edit the series to add tests."
      />

      {series.leaderboardEnabled && (
        <AppDataTable
          fill={false}
          toolbar={<SectionTitle icon={Trophy}>Leaderboard</SectionTitle>}
          columns={boardColumns}
          rows={boardRows}
          rowKey={(r) => r.studentId}
          sort={boardSort}
          onSortChange={(key, direction) => setBoardSort({ key, direction })}
          emptyMessage="No attempts yet. Rankings appear once students start completing tests in this series."
        />
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default TestSeriesDetailsPage;
